import { autoinject, bindable, computedFrom, observable } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import * as Hammer from 'hammerjs';
import { Reflection } from "../../helpers/reflection";


//Load styles
require("./comp-carousel.scss");

@autoinject
@transient()
export class CompCarousel {

    private _translateValue: number = 0;
    private _onDetached: any;
    private _translating: boolean;
    private _mc: Hammer.HammerManager;
    private _sizeCarouselItem: number;
    private _propChangeTimeout: any;
    private _attached: boolean;

    public carouselItems: HTMLDivElement[] = [];

    @observable()
    public infiniteCollection: any[];

    public carouselContent: HTMLDivElement;
    public childContent: HTMLDivElement;
    public currentIndex: number = 0;
    public panning: boolean;
    public canNext: boolean = false;
    public canPrev: boolean = false;

    @observable()
    public trackElement: HTMLDivElement;

    @bindable
    public collection: any[];

    @bindable
    public isVertical: boolean = false;

    @bindable
    public enableTouch: boolean = true;

    @bindable
    public isCircular: boolean = false;

    @computedFrom(Reflection.getPropertyPath<CompCarousel>(z => z._sizeCarouselItem), Reflection.getPropertyPath<CompCarousel>(z => z.carouselItems), 
        Reflection.getPropertyPath<CompCarousel>(z => z.isVertical), Reflection.getPropertyPath<CompCarousel>(z => z.carouselItems.length))
    public get sizeCarouselItem() {
        if (!this._sizeCarouselItem) {
            this._sizeCarouselItem = this.carouselItems[0] ? this.carouselItems[0][this.isVertical ? 'offsetHeight' : 'offsetWidth'] : 0;
        }
        return this._sizeCarouselItem;
    }

    @computedFrom(Reflection.getPropertyPath<CompCarousel>(z => z._translateValue), Reflection.getPropertyPath<CompCarousel>(z => z.isCircular), 
        Reflection.getPropertyPath<CompCarousel>(z => z.sizeCarouselItem), Reflection.getPropertyPath<CompCarousel>(z => z.collection), 
        Reflection.getPropertyPath<CompCarousel>(z => z.collection.length))
    public get distance() {
        return Math.abs(this._translateValue) - (this.isCircular ? (this.sizeCarouselItem * this.collection.length) : 0);
    }

    public constructor(private element: Element) { }

    attached() {
        this._attached = true;

        window.addEventListener('resize', this.onResize);
        if (this.enableTouch) {
            this.setTouchEventsManager();
        }
        this.propertyChanged();
    }

    collectionChanged(value) {
        if (!value) {
            return;
        }
        //Filter items that are not shown
        this.collection = value.filter(item => item.show);
        this.propertyChanged();
    }

    trackElementChanged() {
        if (this.trackElement) {
            this.refreshNavigationHandlers();
        }
    }

    onResize = () => {
        const index = Math.floor(this.distance / this._sizeCarouselItem);
        this._sizeCarouselItem = this.carouselItems[0][this.isVertical ? 'offsetHeight' : 'offsetWidth'];
        if (this.isCircular) {
            this.setTranslateValue(-this.sizeCarouselItem * (index + this.collection.length));
        } else {
            this.setTranslateValue(0);
        }

        this.refreshNavigationHandlers();
    }

    public refreshNavigationHandlers() {
        if (!this.trackElement) {
            return;
        }
        const scrollDir = this.isVertical ? 'scrollHeight' : 'scrollWidth';
        const sizeDir = this.isVertical ? 'offsetHeight' : 'offsetWidth';

        // Simplified condition
        if (this.isCircular) {
            this.canNext = this.canPrev = true;
        } else {
            const scrollPosition = this.trackElement[scrollDir] - this.distance;
            const trackSize = this.trackElement[sizeDir];
            this.canNext = Math.abs(scrollPosition - trackSize) >= trackSize;
            this.canPrev = this.distance > 0;
        }
    }

    private setTouchEventsManager() {
        this._mc = new Hammer.Manager(this.carouselContent);
        this._mc.add(new Hammer.Swipe({ threshold: 50, direction: (this.isVertical ? Hammer.DIRECTION_VERTICAL : Hammer.DIRECTION_HORIZONTAL) }));
        this._mc.add(new Hammer.Tap({ taps: 1, direction: (this.isVertical ? Hammer.DIRECTION_VERTICAL : Hammer.DIRECTION_HORIZONTAL) }));
        this._mc.add(new Hammer.Pan({ threshold: 15, direction: (this.isVertical ? Hammer.DIRECTION_VERTICAL : Hammer.DIRECTION_HORIZONTAL), pointers: 0 })
            .recognizeWith(this._mc.get('swipe'))
            .recognizeWith(this._mc.get('tap')));
        let initialTranslate = null;
        this._mc.on("tap", (ev) => {
            const paramsEvent: any = { detail: {}, bubbles: true };
            const event: CustomEvent = new CustomEvent("tap", paramsEvent);
            ev.srcEvent.target.dispatchEvent(event);
        });
        this._mc.on("swipeleft", () => {
            this.translateToNextItem();
        });
        this._mc.on("swiperight", () => {
            this.translateToPreviousItem();
        });
        this._mc.on("panstart", (ev) => {
            ev.srcEvent.preventDefault();
            ev.srcEvent.stopPropagation();
            this.panning = true;
            this.cancelAnimation();
            this.validateTranslateValue();
            initialTranslate = this._translateValue;
        });
        this._mc.on("panend", (ev) => {
            ev.srcEvent.preventDefault();
            ev.srcEvent.stopPropagation();
            this.panning = false;
            const x = this.isVertical ? ev.deltaY : ev.deltaX;
            if (!this._translating) {
                if (x > this.sizeCarouselItem / 2) {
                    this.translateToNextItem();
                }
                else {
                    this.translateToPreviousItem();
                }
            }
        });
        if (this.isVertical) {
            this._mc.on("panup pandown", (ev) => {
                if (!initialTranslate) {
                    initialTranslate = this._translateValue;
                }
                if (ev.velocityY != 0) {
                    this.setTranslateValue(initialTranslate + ev.deltaY);
                }
            });
        }
        else {
            this._mc.on("panleft panright", (ev) => {
                if (!this._translating) {
                    if (!initialTranslate) {
                        initialTranslate = this._translateValue;
                    }
                    if ((Math.abs(ev.angle) > 170 || Math.abs(ev.angle) < 10) && ev.velocityX != 0) {
                        this.setTranslateValue(initialTranslate + ev.deltaX);
                    }
                }
            });
        }
    }

    enableTouchChanged() {
        if (!this._attached) {
            return;
        }
        this.setTouchEventsManager();
    }

    propertyChanged() {
        if (!this._attached) {
            return;
        }
        this.infiniteCollection = [];
        const collection = this.collection.slice(0);
        if (this.isCircular) {
            collection.unshift(...this.collection.slice(0));
            collection.push(...this.collection.slice(0));
        }
        if (collection[0] && !('index' in collection[0])) {
            collection.forEach(item => {
                item.index = this.collection.indexOf(item);
            });
        }
        this.infiniteCollection = collection;
        if (this._propChangeTimeout) {
            clearTimeout(this._propChangeTimeout);
        }

        this._propChangeTimeout = setTimeout(() => {
            if (this.isCircular) {
                this.canNext = this.canPrev = true;
                const value = -(this.collection.length * this.sizeCarouselItem);
                this.setTranslateValue(value);
            } else {
                this.refreshNavigationHandlers();
                this.setTranslateValue(0);
            }
            this._propChangeTimeout = null;
        }, 120);
    }

    detached() {
        if (this._onDetached) {
            this._onDetached();
        }
        if (this._mc) {
            this._mc.destroy();
            this._mc = null;
        }
        if (this._propChangeTimeout) {
            clearTimeout(this._propChangeTimeout);
        }
        window.removeEventListener('resize', this.onResize);
        this._attached = false;
    }

    translateToNextItem = () => {
        if (this.collection.length <= 1) {
            return;
        }

        const translateValueAbs = Math.abs(this._translateValue);
        const currentItemOffset = translateValueAbs - this.sizeCarouselItem * Math.floor(Math.abs(this._translateValue) / this.sizeCarouselItem);
        this.animateTranslation(this._translateValue - (this.sizeCarouselItem - currentItemOffset));
    }

    translateToPreviousItem = () => {
        if (this.collection.length <= 1) {
            return;
        }
        const translateValueAbs = Math.abs(this._translateValue);
        const currentItemOffset = translateValueAbs - this.sizeCarouselItem * Math.floor(Math.abs(this._translateValue) / this.sizeCarouselItem);
        this.animateTranslation(this._translateValue + (currentItemOffset != 0 ? currentItemOffset : this.sizeCarouselItem));
    }

    animateTranslation = (translateValue: number) => {
        this._translating = true;
        requestAnimationFrame(() => {
            this.trackElement.removeEventListener("transitionend", this.onAnimationEnd, false);
            this.scheduleAnimation();
            if (this.setTranslateValue(translateValue)) {
                this.trackElement.addEventListener("transitionend", this.onAnimationEnd, false);
                this._translating = false;
            } else {
                this._translating = false;
            }
        });
    }

    setTranslateValue(value: number): boolean {
        if (this.collection.length <= 1) {
            return false;
        }
        if (!this.isCircular) {
            if ((this.trackElement.scrollWidth - this.trackElement.offsetWidth) < Math.abs(value) || value > 0 ||
                this.trackElement.scrollWidth <= this.trackElement.offsetWidth) {
                if ((this.trackElement.scrollWidth - this.trackElement.offsetWidth) < Math.abs(value)) {
                    value = -(this.trackElement.scrollWidth - this.trackElement.offsetWidth);
                } else {
                    return false;
                }
            }
        }

        this._translateValue = value;
        if (!this.isVertical) {
            this.trackElement.style.transform = `translate3d(${this._translateValue}px,0,0)`;
        } else {
            this.trackElement.style.transform = `translate3d(0,${this._translateValue}px,0)`;
        }
        const index = Math.round((this.infiniteCollection.length - 1) * Math.abs(this._translateValue) / (this.trackElement.scrollWidth - this.trackElement.offsetWidth));
        if (index >= 0 && index < this.infiniteCollection.length) {
            const oldValue = this.currentIndex;
            this.currentIndex = this.infiniteCollection[index].index;
            const paramsEvent: any = { detail: { oldValue: this.collection[oldValue], newValue: this.collection[this.currentIndex] }, bubbles: true };
            const changeTextEvent: CustomEvent = new CustomEvent("item-changed", paramsEvent);
            this.element.dispatchEvent(changeTextEvent);
        }
        this.refreshNavigationHandlers();
        return true;

    }

    scheduleAnimation() {
        this.trackElement.style.transition = `transform ${500}ms`;
        this.trackElement.style.webkitTransition = `transform ${500}ms`;
    }

    removeAnimation() {
        this.trackElement.style.transition = null;
        this.trackElement.style.webkitTransition = null;
        this._translating = false;
    }

    cancelAnimation() {
        const tranform = this.getComputedTranslateXY(this.trackElement);
        if (Array.isArray(tranform)) {
            this.setTranslateValue(this.isVertical ? tranform[1] : tranform[0]);
        } else {
            this.setTranslateValue(tranform);
        }
        this.removeAnimation();
        this.validateTranslateValue();
    }

    getComputedTranslateXY(obj) {
        const transArr = [];
        if (!window.getComputedStyle) return;
        const style = getComputedStyle(obj),
            transform = style.transform || style.webkitTransform || style["mozTransform"];
        let mat = transform.match(/^matrix3d\((.+)\)$/);
        if (mat) return parseFloat(mat[1].split(', ')[this.isVertical ? 13 : 12]);
        mat = transform.match(/^matrix\((.+)\)$/);
        mat ? transArr.push(parseFloat(mat[1].split(', ')[4])) : 0;
        mat ? transArr.push(parseFloat(mat[1].split(', ')[5])) : 0;
        return transArr;
    }

    onAnimationEnd = () => {
        this.removeAnimation();
        this.trackElement.removeEventListener("transitionend", this.onAnimationEnd, false);
        setTimeout(() => {
            this.validateTranslateValue();
        });
    }

    validateTranslateValue() {
        if (this.isCircular) {
            const treshold = this.sizeCarouselItem * this.collection.length;
            if (this.distance >= treshold) {
                this.setTranslateValue(-this.distance);
            } else if (this.distance < 0) {
                this.setTranslateValue(-(2 * treshold + this.distance));
            }
        }
    }

    next(): void {
        if (!this._translating && this.canNext) {
            this.translateToNextItem();
        }
    }

    prev(): void {
        if (!this._translating && this.canPrev) {
            this.translateToPreviousItem();
        }
    }

    changeIndex(index: number, animate: boolean = true) {
        const treshold = this.isCircular ? this.sizeCarouselItem * this.collection.length : 0;
        const translateValue = -(treshold + this.sizeCarouselItem * index);
        if (this._propChangeTimeout) {
            clearTimeout(this._propChangeTimeout);
        }
        if (animate) {
            this.animateTranslation(translateValue);
        } else {
            this.setTranslateValue(translateValue)
        }
    }
}
